import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import MetaTags from '../common/MetaTags';

export default function HelpArticleView({ slug, onNavigate }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Add click handlers to images for lightbox using event delegation
  useEffect(() => {
    if (!article) return;

    const contentContainer = document.querySelector('.help-article-content');
    if (!contentContainer) return;

    const handleClick = (e) => {
      if (e.target.tagName === 'IMG') {
        setLightboxImage(e.target.src);
      }
    };

    // Style all images with pointer cursor
    const images = contentContainer.querySelectorAll('img');
    images.forEach(img => {
      img.style.cursor = 'pointer';
    });

    contentContainer.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      contentContainer.removeEventListener('click', handleClick);
    };
  }, [article]);

  async function loadArticle() {
    try {
      setLoading(true);
      setNotFound(false);

      const q = query(
        collection(db, 'help_articles'),
        where('slug', '==', slug),
        where('published', '==', true)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const articleData = {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };

      setArticle(articleData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading help article:', error);
      setNotFound(true);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <BlogLayout showSidebar={false} whiteBackground={true}>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div style={{ fontSize: '16px', color: 'var(--muted)' }}>
            Loading...
          </div>
        </div>
      </BlogLayout>
    );
  }

  if (notFound) {
    return (
      <BlogLayout showSidebar={false} whiteBackground={true}>
        <MetaTags
          title="Help Article Not Found"
          description="The help article you're looking for doesn't exist."
          type="website"
        />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>404</h1>
          <p style={{ fontSize: '18px', color: 'var(--muted)', marginBottom: '32px' }}>
            Help article not found
          </p>
          <a
            href="/help"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              background: 'var(--primary)',
              color: 'white',
              borderRadius: 'var(--radius-sm)',
              textDecoration: 'none',
              fontWeight: '600'
            }}
          >
            Back to Help Center
          </a>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout showSidebar={false} whiteBackground={true}>
      <MetaTags
        title={article.title}
        description={article.description || article.title}
        type="article"
      />

      <style>{`
        .help-article-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .help-breadcrumb {
          margin-bottom: 32px;
          font-size: 14px;
          color: var(--muted);
        }

        .help-breadcrumb a {
          color: var(--primary);
          text-decoration: none;
        }

        .help-breadcrumb a:hover {
          text-decoration: underline;
        }

        .help-article-header {
          margin-bottom: 48px;
          padding-bottom: 32px;
          border-bottom: 1px solid var(--border);
        }

        .help-article-category {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 16px;
          background: var(--primary-light);
          color: var(--primary);
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
        }

        .help-article-title {
          font-size: 42px;
          font-weight: 800;
          margin-bottom: 16px;
          color: var(--text);
          line-height: 1.2;
        }

        .help-article-description {
          font-size: 18px;
          color: var(--muted);
          line-height: 1.6;
        }

        .help-article-content {
          font-size: 17px;
          line-height: 1.8;
          color: var(--text);
        }

        .help-article-content h2 {
          font-size: 32px;
          font-weight: 700;
          margin: 48px 0 24px;
          color: var(--text);
        }

        .help-article-content h3 {
          font-size: 24px;
          font-weight: 600;
          margin: 32px 0 16px;
          color: var(--text);
        }

        .help-article-content p {
          margin-bottom: 20px;
        }

        .help-article-content ul,
        .help-article-content ol {
          margin: 20px 0 20px 30px;
        }

        .help-article-content li {
          margin-bottom: 10px;
        }

        .help-article-content a {
          color: var(--primary);
          text-decoration: underline;
        }

        .help-article-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 32px auto;
          border: 1px solid #e3e2df;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
          display: block;
        }

        /* Handle consecutive images (direct siblings) */
        .help-article-content img + img {
          margin-left: 16px;
          display: inline-block;
          vertical-align: top;
          max-width: calc(50% - 8px);
        }

        .help-article-content img:has(+ img) {
          display: inline-block;
          vertical-align: top;
          max-width: calc(50% - 8px);
        }

        /* Handle images in consecutive paragraphs */
        .help-article-content p:has(img) + p:has(img) {
          display: inline-block;
          vertical-align: top;
          width: calc(50% - 8px);
          margin-left: 16px;
          margin-top: 32px;
        }

        .help-article-content p:has(img):has(+ p:has(img)) {
          display: inline-block;
          vertical-align: top;
          width: calc(50% - 8px);
          margin-top: 32px;
        }

        .help-article-content p:has(img) img {
          margin: 0;
        }

        .help-article-content hr {
          border: none;
          height: 1px;
          background: #e3e2df;
          margin: 48px 0;
        }

        .help-article-content blockquote {
          border-left: 4px solid var(--primary);
          padding-left: 20px;
          margin: 24px 0;
          color: var(--muted);
          font-style: italic;
        }

        .help-article-content code {
          background: var(--bg);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
          font-family: 'Courier New', monospace;
        }

        .help-article-content pre {
          background: var(--bg);
          padding: 16px;
          border-radius: var(--radius-sm);
          overflow-x: auto;
          margin: 24px 0;
        }

        .help-article-content pre code {
          background: none;
          padding: 0;
        }

        .help-article-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 32px 0;
        }

        .help-article-content table th,
        .help-article-content table td {
          padding: 16px;
          border: 1px solid #e3e2df;
          text-align: left;
        }

        .help-article-content table th {
          background: #37352f;
          color: white;
          font-weight: 600;
        }

        .help-article-content table td {
          background: white;
          vertical-align: top;
        }

        /* Remove hover effects on table cells */
        .help-article-content table td:hover {
          background: white;
        }

        /* Remove borders from tables containing images */
        .help-article-content table:has(img) {
          border: none;
        }

        .help-article-content table:has(img) th,
        .help-article-content table:has(img) td {
          border: none;
        }

        .help-article-footer {
          margin-top: 80px;
          padding-top: 40px;
          border-top: 1px solid var(--border);
        }

        .help-footer-cta {
          background: var(--primary-light);
          padding: 32px;
          border-radius: var(--radius-lg);
          text-align: center;
        }

        .help-footer-cta h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .help-footer-cta p {
          font-size: 15px;
          color: var(--muted);
          margin-bottom: 20px;
        }

        /* Lightbox */
        .lightbox-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          cursor: pointer;
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .lightbox-image {
          max-width: 90%;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 4px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        }

        .lightbox-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .lightbox-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        @media (max-width: 768px) {
          .help-article-title {
            font-size: 32px;
          }

          .help-article-description {
            font-size: 16px;
          }

          .help-article-content {
            font-size: 16px;
          }

          .help-article-content h2 {
            font-size: 24px;
          }

          .help-article-content h3 {
            font-size: 20px;
          }

          /* Stack images on mobile */
          .help-article-content img + img,
          .help-article-content img:has(+ img) {
            display: block;
            max-width: 100%;
            margin-left: 0;
          }

          .help-article-content p:has(img) + p:has(img),
          .help-article-content p:has(img):has(+ p:has(img)) {
            display: block;
            width: 100%;
            margin-left: 0;
          }

          .help-article-content p:has(img) img {
            margin: 32px 0;
          }
        }
      `}</style>

      <div className="help-article-container">
        {/* Breadcrumb */}
        <div className="help-breadcrumb">
          <a href="/help">Help Center</a>
          {article.category && <span> / {article.category}</span>}
        </div>

        {/* Header */}
        <header className="help-article-header">
          {article.category && (
            <div className="help-article-category">{article.category}</div>
          )}
          <h1 className="help-article-title">{article.title}</h1>
          {article.description && (
            <p className="help-article-description">{article.description}</p>
          )}
        </header>

        {/* Content */}
        <div
          className="help-article-content"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Footer CTA */}
        <footer className="help-article-footer">
          <div className="help-footer-cta">
            <h3>Was this article helpful?</h3>
            <p>If you still have questions, feel free to reach out to our support team.</p>
            <a
              href="/contact-us"
              style={{
                display: 'inline-block',
                padding: '12px 32px',
                background: 'var(--primary)',
                color: 'white',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--primary-hover)'}
              onMouseLeave={(e) => e.target.style.background = 'var(--primary)'}
            >
              Contact Support
            </a>
          </div>

          <div style={{ marginTop: '32px', textAlign: 'center' }}>
            <a
              href="/help"
              style={{
                color: 'var(--primary)',
                textDecoration: 'none',
                fontSize: '15px'
              }}
            >
              ← Back to Help Center
            </a>
          </div>
        </footer>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="lightbox-close"
            onClick={() => setLightboxImage(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Enlarged view"
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </BlogLayout>
  );
}
